import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '@dukene/shared';
import {
  Store, Mail, Lock, User, AlertCircle, ArrowRight, ArrowLeft,
  Award, TrendingUp, Clock, Phone, Hash, Eye, EyeOff,
  MapPin, Building2, Check,
} from 'lucide-react';
import { renderGoogleIdentityButton } from '../../lib/googleIdentity';

// ── Schemas per step ────────────────────────────────────────────────────────

const step1Schema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string(),
}).refine((d) => d.password === d.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

const step2Schema = z.object({
  organisationName: z.string().optional(),
  storeName: z.string().min(2, 'Store name must be at least 2 characters'),
  storeSubdomain: z.string()
    .min(3, 'Subdomain must be at least 3 characters')
    .regex(/^[a-z0-9-]+$/, 'Only lowercase letters, numbers, and hyphens'),
  storePhone: z.string().optional(),
});

const step3Schema = z.object({
  streetAddress: z.string().min(3, 'Please enter your street address'),
  city: z.string().min(2, 'Please enter your city'),
  county: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().length(2, 'Use a 2-letter country code e.g. KE'),
});

type Step1Data = z.infer<typeof step1Schema>;
type Step2Data = z.infer<typeof step2Schema>;
type Step3Data = z.infer<typeof step3Schema>;

// ── Step indicators ─────────────────────────────────────────────────────────

const STEPS = [
  { number: 1, label: 'Your Account' },
  { number: 2, label: 'Your Store' },
  { number: 3, label: 'Business Address' },
];

// ── Left panel content per step ─────────────────────────────────────────────

const LEFT_CONTENT = [
  {
    heading: ['Start Your', 'Business'],
    body: 'Create your online store today and start selling to customers across Kenya.',
    hint: null,
  },
  {
    heading: ['Name Your', 'Store'],
    body: 'Your store name and URL are how customers find and remember you online.',
    hint: null,
  },
  {
    heading: ['Your Business', 'Address'],
    body: 'Required for Google Shopping approval. Without it, your products won\'t appear on Google.',
    hint: '🛡️ This is how Google verifies you\'re a real, legitimate business. It keeps your products on Google Shopping.',
  },
];

const features = [
  { icon: Award, text: 'Easy store setup' },
  { icon: TrendingUp, text: 'Powerful analytics' },
  { icon: Clock, text: '24/7 support' },
];

// ── Main component ───────────────────────────────────────────────────────────

export default function Register() {
  const navigate = useNavigate();
  const registerFn = useAuthStore((state) => state.register);
  const registerWithGoogle = useAuthStore((state) => state.registerWithGoogle);

  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [googleCredential, setGoogleCredential] = useState<string | null>(null);
  const [isPendingVerification, setIsPendingVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const googleInitializedRef = useRef(false);
  const googleCleanupRef = useRef<(() => void) | null>(null);
  const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';

  // Accumulated data across steps
  const [step1Data, setStep1Data] = useState<Step1Data | null>(null);
  const [step2Data, setStep2Data] = useState<Step2Data | null>(null);

  // ── Step 1 form
  const form1 = useForm<Step1Data>({ resolver: zodResolver(step1Schema), defaultValues: step1Data ?? {} });
  // ── Step 2 form
  const form2 = useForm<Step2Data>({ resolver: zodResolver(step2Schema), defaultValues: step2Data ?? {} });
  // ── Step 3 form
  const form3 = useForm<Step3Data>({
    resolver: zodResolver(step3Schema),
    defaultValues: { country: 'KE' },
  });

  const checkAvailability = async (values: { email?: string; subdomain?: string }) => {
    const response = await fetch(`${API_URL}/auth/registration/availability`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(values),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(result.error?.message || result.message || 'Unable to verify availability. Please try again.');
    }
    return result.data as {
      email?: { available: boolean };
      subdomain?: { available: boolean };
    };
  };

  useEffect(() => {
    if (step !== 1) {
      googleInitializedRef.current = false;
      return;
    }
    if (googleInitializedRef.current) return;
    let cancelled = false;
    const initializeGoogle = async () => {
      try {
        const response = await fetch(`${API_URL}/auth/google/client-id`);
        if (!response.ok) return;
        const result = await response.json();
        const clientId = result.data?.clientId;
        if (!clientId || cancelled) return;
        if (!cancelled && googleButtonRef.current && !googleInitializedRef.current) {
          const cleanup = await renderGoogleIdentityButton(
            googleButtonRef.current,
            clientId,
            async ({ credential }) => {
              try {
                const payload = JSON.parse(atob(credential.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
                const availability = await checkAvailability({ email: payload.email });
                if (!availability.email?.available) {
                  setGoogleCredential(null);
                  setError('An account already uses this Google email. Please sign in instead.');
                  return;
                }
                const names = String(payload.name || '').trim().split(/\s+/);
                setStep1Data({
                  firstName: payload.given_name || names[0] || 'Merchant',
                  lastName: payload.family_name || names.slice(1).join(' ') || 'Owner',
                  email: payload.email,
                  password: '',
                  confirmPassword: '',
                });
                setGoogleCredential(credential);
                setError('');
                setStep(2);
              } catch (error: any) {
                setError(error.message || 'Google account details could not be verified. Please try again.');
              }
            },
            'signup_with',
          );
          if (cancelled) {
            cleanup();
            return;
          }
          googleCleanupRef.current = cleanup;
          googleInitializedRef.current = true;
        }
      } catch {
        // Email/password registration remains available.
      }
    };
    initializeGoogle();
    return () => {
      cancelled = true;
      googleCleanupRef.current?.();
      googleCleanupRef.current = null;
      googleInitializedRef.current = false;
    };
  }, [step]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleStep1 = async (data: Step1Data) => {
    try {
      setIsLoading(true);
      setError('');
      const availability = await checkAvailability({ email: data.email.trim().toLowerCase() });
      if (!availability.email?.available) {
        setError('This email is already registered. Please sign in or use a different email.');
        return;
      }
      setGoogleCredential(null);
      setStep1Data({ ...data, email: data.email.trim().toLowerCase() });
      setStep(2);
    } catch (err: any) {
      setError(err.message || 'Unable to check this email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStep2 = async (data: Step2Data) => {
    try {
      setIsLoading(true);
      setError('');
      const subdomain = data.storeSubdomain.trim().toLowerCase();
      const availability = await checkAvailability({ subdomain });
      if (!availability.subdomain?.available) {
        setError(`The store address ${subdomain}.dukene.com is already taken. Please choose another.`);
        return;
      }
      setStep2Data({ ...data, storeSubdomain: subdomain });
      setStep(3);
    } catch (err: any) {
      setError(err.message || 'Unable to check this store address. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStep3 = async (data: Step3Data) => {
    if (!step1Data || !step2Data) return;
    try {
      setError('');
      setIsLoading(true);
      const businessData = {
        organisationName: step2Data.organisationName || step2Data.storeName,
        storeName: step2Data.storeName,
        storeSubdomain: step2Data.storeSubdomain.toLowerCase(),
        storePhone: step2Data.storePhone,
        streetAddress: data.streetAddress,
        city: data.city,
        county: data.county,
        postalCode: data.postalCode,
        country: data.country || 'KE',
      };
      if (googleCredential) {
        await registerWithGoogle({ credential: googleCredential, ...businessData });
        navigate('/');
      } else {
        const result = await registerFn({
          email: step1Data.email,
          password: step1Data.password,
          firstName: step1Data.firstName,
          lastName: step1Data.lastName,
          role: 'MERCHANT',
          ...businessData,
        });

        if (result?.requiresVerification) {
          setRegisteredEmail(step1Data.email);
          setIsPendingVerification(true);
          return;
        }

        navigate('/');
      }
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setResendStatus('loading');
      setError('');
      const response = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: registeredEmail }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error?.message || data.message || 'Failed to resend verification email');
      }
      setResendStatus('success');
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification email.');
      setResendStatus('idle');
    }
  };

  const leftContent = isPendingVerification
    ? {
        heading: ['Verify Your', 'Email'],
        body: 'We sent a verification link to your email address. Verify your email to activate your store and sign in to your dashboard.',
        hint: 'Email verification keeps your account and your store secure.',
      }
    : LEFT_CONTENT[step - 1];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen flex bg-white">

      {/* ── Left branding panel ────────────────────────────────────────────── */}
      <div className="hidden lg:flex lg:w-1/2 bg-white border-r border-gray-100 relative">
        <div className="flex flex-col justify-between w-full p-16 xl:p-24">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
              <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain p-0.5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-black tracking-tight">Dukene</h1>
              <p className="text-xs text-gray-500 font-medium tracking-wide">MERCHANTS</p>
            </div>
          </div>

          {/* Dynamic content */}
          <div className="space-y-16">
            <div>
              <h2 className="text-6xl font-light text-black mb-6 leading-tight tracking-tight">
                {leftContent.heading[0]}
                <br />
                {leftContent.heading[1]}
              </h2>
              <div className="w-16 h-0.5 bg-black mb-6" />
              <p className="text-lg text-gray-600 leading-relaxed font-light">{leftContent.body}</p>

              {leftContent.hint && (
                <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
                  <p className="text-sm text-amber-800 font-light leading-relaxed">{leftContent.hint}</p>
                </div>
              )}
            </div>

            {/* Features (step 1 only) */}
            {step === 1 && !isPendingVerification && (
              <div className="space-y-6">
                <p className="text-xs text-gray-400 font-medium tracking-widest uppercase">What You Get</p>
                <div className="space-y-5">
                  {features.map((f, i) => {
                    const Icon = f.icon;
                    return (
                      <div key={i} className="flex items-center gap-4">
                        <Icon className="w-5 h-5 text-black flex-shrink-0" />
                        <p className="text-base text-gray-700 font-light">{f.text}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Stats (step 1 only) */}
            {step === 1 && !isPendingVerification && (
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <div className="text-4xl font-light text-black mb-2">5K+</div>
                  <div className="text-sm text-gray-500 font-light">Active Merchants</div>
                </div>
                <div>
                  <div className="text-4xl font-light text-black mb-2">100K+</div>
                  <div className="text-sm text-gray-500 font-light">Products Sold</div>
                </div>
              </div>
            )}

            {/* Step 2: GMC preview */}
            {step === 2 && !isPendingVerification && (
              <div className="space-y-6">
                <p className="text-xs text-gray-400 font-medium tracking-widest uppercase">Your Store URL</p>
                <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                  <p className="text-sm text-gray-400 mb-1 font-light">Will be available at:</p>
                  <p className="text-lg font-medium text-black break-all">
                    {form2.watch('storeSubdomain') || 'yourstore'}.dukene.com
                  </p>
                </div>
              </div>
            )}

            {/* Step 3: GMC compliance note */}
            {step === 3 && !isPendingVerification && (
              <div className="space-y-4">
                <p className="text-xs text-gray-400 font-medium tracking-widest uppercase">Google Shopping Requirements</p>
                {['Valid business address', 'Privacy policy page', 'Return/refund policy', 'Contact information'].map((req) => (
                  <div key={req} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald-600" />
                    </div>
                    <p className="text-sm text-gray-600 font-light">{req}</p>
                  </div>
                ))}
                <p className="text-xs text-gray-400 font-light mt-2">
                  Policy pages are automatically created for your store. The address is what we're collecting now.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center gap-8 text-xs text-gray-400 font-light">
            <span>© 2025 Dukene</span>
            <span className="w-1 h-1 rounded-full bg-gray-300" />
            <span>All rights reserved</span>
          </div>
        </div>
      </div>

      {/* ── Right form panel ───────────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center bg-white px-8 lg:px-20 xl:px-32 py-12">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-12">
            <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
              <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain p-0.5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-black">Dukene</h1>
              <p className="text-xs text-gray-500 font-medium">MERCHANTS</p>
            </div>
          </div>

          {/* Step indicators */}
          {!isPendingVerification && (
            <div className="flex items-center gap-0 mb-10">
              {STEPS.map((s, idx) => {
                const done = step > s.number;
                const active = step === s.number;
                return (
                  <div key={s.number} className="flex items-center flex-1 last:flex-none">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${done
                          ? 'bg-black text-white'
                          : active
                            ? 'bg-black text-white ring-4 ring-gray-200'
                            : 'bg-gray-100 text-gray-400'
                          }`}
                      >
                        {done ? <Check className="w-4 h-4" /> : s.number}
                      </div>
                      <span className={`text-[10px] font-medium mt-1 whitespace-nowrap ${active ? 'text-black' : 'text-gray-400'}`}>
                        {s.label}
                      </span>
                    </div>
                    {idx < STEPS.length - 1 && (
                      <div className={`h-px flex-1 mx-2 mb-4 transition-colors ${done ? 'bg-black' : 'bg-gray-200'}`} />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Step heading */}
          {!isPendingVerification && (
            <div className="mb-8">
              <h2 className="text-3xl font-light text-black mb-2 tracking-tight">
                {step === 1 && 'Create Account'}
                {step === 2 && 'Your Store'}
                {step === 3 && 'Business Address'}
              </h2>
              <p className="text-gray-500 text-sm font-light">
                {step === 1 && 'Set up your personal login credentials'}
                {step === 2 && 'Give your store an identity'}
                {step === 3 && 'Required for Google Merchant Center approval'}
              </p>
            </div>
          )}

          {/* Error alert */}
          {error && (
            <div className="mb-6 border border-red-200 rounded-lg p-4 bg-red-50">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-900 text-sm mb-1">Error</p>
                  <p className="text-sm text-red-700 font-light">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* ── Pending Verification Screen ─────────────────────────────────── */}
          {isPendingVerification && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-50 border border-amber-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-8 h-8 text-amber-600" />
                </div>
                <h2 className="text-3xl font-light text-black mb-3 tracking-tight">Check your email</h2>
                <p className="text-gray-600 text-sm font-light leading-relaxed mb-2">
                  Your store <span className="font-medium text-black">{step2Data?.storeName || 'store'}</span> has been created!
                </p>
                <p className="text-gray-500 text-sm font-light leading-relaxed">
                  We've sent a verification link to <span className="font-medium text-black">{registeredEmail}</span>. Please verify your email address to access your merchant dashboard.
                </p>
              </div>

              <div className="p-4 bg-gray-50 border border-gray-100 rounded-xl space-y-2 text-xs text-gray-500 font-light">
                <p className="font-medium text-gray-700">Next Steps:</p>
                <p>1. Open the email from <span className="text-black font-medium">onboarding@dukene.co.ke</span></p>
                <p>2. Click the <span className="text-black font-medium">"Verify My Email"</span> button</p>
                <p>3. Sign in to your merchant dashboard</p>
              </div>

              {resendStatus === 'success' ? (
                <div className="flex items-center justify-center gap-2 text-green-700 text-xs font-medium bg-green-50 py-3 px-4 rounded-lg border border-green-200">
                  <Check className="w-4 h-4 text-green-600" />
                  Verification email resent successfully!
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendStatus === 'loading'}
                  className="w-full h-11 bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 text-sm font-medium rounded-lg transition-all disabled:opacity-50"
                >
                  {resendStatus === 'loading' ? 'Sending...' : 'Resend Verification Email'}
                </button>
              )}

              <Link
                to="/login"
                className="w-full h-11 bg-black hover:bg-gray-900 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2 text-sm"
              >
                Go to Sign In <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {/* ── STEP 1: Account ─────────────────────────────────────────────── */}
          {!isPendingVerification && step === 1 && (
            <form onSubmit={form1.handleSubmit(handleStep1)} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-black mb-2">First Name</label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      {...form1.register('firstName')}
                      placeholder="John"
                      className="h-11 w-full rounded-lg border border-gray-200 bg-white text-black placeholder:text-gray-400 focus:border-black focus:ring-1 focus:ring-black focus:outline-none pl-10 pr-4 text-sm font-light transition-all"
                    />
                  </div>
                  {form1.formState.errors.firstName && (
                    <p className="text-xs text-red-600 mt-1.5">{form1.formState.errors.firstName.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-2">Last Name</label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      {...form1.register('lastName')}
                      placeholder="Doe"
                      className="h-11 w-full rounded-lg border border-gray-200 bg-white text-black placeholder:text-gray-400 focus:border-black focus:ring-1 focus:ring-black focus:outline-none pl-10 pr-4 text-sm font-light transition-all"
                    />
                  </div>
                  {form1.formState.errors.lastName && (
                    <p className="text-xs text-red-600 mt-1.5">{form1.formState.errors.lastName.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    {...form1.register('email')}
                    type="email"
                    placeholder="you@example.com"
                    className="h-11 w-full rounded-lg border border-gray-200 bg-white text-black placeholder:text-gray-400 focus:border-black focus:ring-1 focus:ring-black focus:outline-none pl-10 pr-4 text-sm font-light transition-all"
                  />
                </div>
                {form1.formState.errors.email && (
                  <p className="text-xs text-red-600 mt-1.5">{form1.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    {...form1.register('password')}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    className="h-11 w-full rounded-lg border border-gray-200 bg-white text-black focus:border-black focus:ring-1 focus:ring-black focus:outline-none pl-10 pr-10 text-sm font-light transition-all"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {form1.formState.errors.password && (
                  <p className="text-xs text-red-600 mt-1.5">{form1.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    {...form1.register('confirmPassword')}
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    className="h-11 w-full rounded-lg border border-gray-200 bg-white text-black focus:border-black focus:ring-1 focus:ring-black focus:outline-none pl-10 pr-10 text-sm font-light transition-all"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} tabIndex={-1}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {form1.formState.errors.confirmPassword && (
                  <p className="text-xs text-red-600 mt-1.5">{form1.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <button type="submit" disabled={isLoading}
                className="w-full h-11 bg-black hover:bg-gray-900 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2 text-sm mt-4 disabled:cursor-wait disabled:opacity-50">
                {isLoading ? 'Checking email...' : <>Continue <ArrowRight className="w-4 h-4" /></>}
              </button>
              <div className="relative py-1">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100" /></div>
                <div className="relative flex justify-center text-xs"><span className="bg-white px-4 text-gray-400">OR</span></div>
              </div>
              <div ref={googleButtonRef} className="flex min-h-11 w-full justify-center overflow-hidden" />
            </form>
          )}

          {/* ── STEP 2: Store ────────────────────────────────────────────────── */}
          {!isPendingVerification && step === 2 && (
            <form onSubmit={form2.handleSubmit(handleStep2)} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  Organisation Name <span className="text-gray-400 font-light">(Optional)</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    {...form2.register('organisationName')}
                    placeholder="e.g. Acme Enterprises Ltd"
                    className="h-11 w-full rounded-lg border border-gray-200 bg-white text-black placeholder:text-gray-400 focus:border-black focus:ring-1 focus:ring-black focus:outline-none pl-10 pr-4 text-sm font-light transition-all"
                  />
                </div>
                {form2.formState.errors.organisationName && (
                  <p className="text-xs text-red-600 mt-1.5">{form2.formState.errors.organisationName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Store Name</label>
                <div className="relative">
                  <Store className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    {...form2.register('storeName')}
                    placeholder="e.g. Nairobi Electronics"
                    className="h-11 w-full rounded-lg border border-gray-200 bg-white text-black placeholder:text-gray-400 focus:border-black focus:ring-1 focus:ring-black focus:outline-none pl-10 pr-4 text-sm font-light transition-all"
                    onChange={(e) => {
                      form2.register('storeName').onChange(e);
                      const generated = e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]/g, '-')
                        .replace(/-+/g, '-')
                        .replace(/^-|-$/g, '');
                      form2.setValue('storeSubdomain', generated);
                    }}
                  />
                </div>
                {form2.formState.errors.storeName && (
                  <p className="text-xs text-red-600 mt-1.5">{form2.formState.errors.storeName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Store Address (Subdomain)</label>
                <div className="flex">
                  <div className="relative flex-1">
                    <Hash className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      {...form2.register('storeSubdomain')}
                      placeholder="nairobi-electronics"
                      className="h-11 w-full rounded-l-lg border border-r-0 border-gray-200 bg-white text-black placeholder:text-gray-400 focus:border-black focus:ring-1 focus:ring-black focus:outline-none pl-10 pr-4 text-sm font-light transition-all"
                    />
                  </div>
                  <span className="h-11 px-4 border border-gray-200 bg-gray-50 text-gray-500 rounded-r-lg flex items-center text-sm font-light">
                    .dukene.com
                  </span>
                </div>
                {form2.formState.errors.storeSubdomain && (
                  <p className="text-xs text-red-600 mt-1.5">{form2.formState.errors.storeSubdomain.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Store Phone Number <span className="text-gray-400 font-light">(Optional)</span></label>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    {...form2.register('storePhone')}
                    type="tel"
                    placeholder="0712 345 678"
                    className="h-11 w-full rounded-lg border border-gray-200 bg-white text-black placeholder:text-gray-400 focus:border-black focus:ring-1 focus:ring-black focus:outline-none pl-10 pr-4 text-sm font-light transition-all"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-4">
                <button type="button" onClick={() => setStep(1)}
                  className="h-11 px-5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-all flex items-center gap-2 text-sm font-medium">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button type="submit" disabled={isLoading}
                  className="flex-1 h-11 bg-black hover:bg-gray-900 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2 text-sm disabled:cursor-wait disabled:opacity-50">
                  {isLoading ? 'Checking address...' : <>Continue <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            </form>
          )}

          {/* ── STEP 3: Address ──────────────────────────────────────────────── */}
          {!isPendingVerification && step === 3 && (
            <form onSubmit={form3.handleSubmit(handleStep3)} className="space-y-5">
              {/* GMC badge */}
              <div className="flex items-center gap-2.5 p-3 bg-blue-50 border border-blue-100 rounded-xl">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#2563eb" />
                  </svg>
                </div>
                <p className="text-xs text-blue-700 font-medium">
                  Required by Google to show your products on Google Shopping
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Street Address</label>
                <div className="relative">
                  <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    {...form3.register('streetAddress')}
                    placeholder="123 Moi Avenue"
                    className="h-11 w-full rounded-lg border border-gray-200 bg-white text-black placeholder:text-gray-400 focus:border-black focus:ring-1 focus:ring-black focus:outline-none pl-10 pr-4 text-sm font-light transition-all"
                  />
                </div>
                {form3.formState.errors.streetAddress && (
                  <p className="text-xs text-red-600 mt-1.5">{form3.formState.errors.streetAddress.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-black mb-2">City</label>
                  <div className="relative">
                    <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      {...form3.register('city')}
                      placeholder="Nairobi"
                      className="h-11 w-full rounded-lg border border-gray-200 bg-white text-black placeholder:text-gray-400 focus:border-black focus:ring-1 focus:ring-black focus:outline-none pl-10 pr-4 text-sm font-light transition-all"
                    />
                  </div>
                  {form3.formState.errors.city && (
                    <p className="text-xs text-red-600 mt-1.5">{form3.formState.errors.city.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-2">County / State <span className="text-gray-400 font-light">(Optional)</span></label>
                  <input
                    {...form3.register('county')}
                    placeholder="Nairobi County"
                    className="h-11 w-full rounded-lg border border-gray-200 bg-white text-black placeholder:text-gray-400 focus:border-black focus:ring-1 focus:ring-black focus:outline-none px-4 text-sm font-light transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-black mb-2">Postal Code <span className="text-gray-400 font-light">(Optional)</span></label>
                  <input
                    {...form3.register('postalCode')}
                    placeholder="00100"
                    className="h-11 w-full rounded-lg border border-gray-200 bg-white text-black placeholder:text-gray-400 focus:border-black focus:ring-1 focus:ring-black focus:outline-none px-4 text-sm font-light transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-2">Country Code</label>
                  <input
                    {...form3.register('country')}
                    placeholder="KE"
                    maxLength={2}
                    className="h-11 w-full rounded-lg border border-gray-200 bg-white text-black placeholder:text-gray-400 focus:border-black focus:ring-1 focus:ring-black focus:outline-none px-4 text-sm font-light transition-all uppercase"
                    onChange={(e) => {
                      e.target.value = e.target.value.toUpperCase().slice(0, 2);
                      form3.register('country').onChange(e);
                    }}
                  />
                  {form3.formState.errors.country && (
                    <p className="text-xs text-red-600 mt-1.5">{form3.formState.errors.country.message}</p>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-4">
                <button type="button" onClick={() => setStep(2)}
                  className="h-11 px-5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-all flex items-center gap-2 text-sm font-medium">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button type="submit" disabled={isLoading}
                  className="flex-1 h-11 bg-black hover:bg-gray-900 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  {isLoading ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Creating store…</span></>
                  ) : (
                    <>Create My Store <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Terms and Privacy Policy Notice */}
          {!isPendingVerification && (
            <p className="mt-6 text-center text-xs text-gray-500 font-light leading-relaxed">
              By submitting the details, the user has accepted the{' '}
              <a
                href="https://dukene.co.ke/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-black transition-colors"
              >
                terms
              </a>{' '}
              and{' '}
              <a
                href="https://dukene.co.ke/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-black transition-colors"
              >
                privacy policy
              </a>
              .
            </p>
          )}

          {/* Sign in link */}
          {!isPendingVerification && (
            <div className="mt-6 pt-6 border-t border-gray-100 text-center">
              <Link to="/login"
                className="inline-flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-black font-light group transition-colors">
                Already have an account? Sign in
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
